import { AIResponseMode, AIResponseModeValue } from '@vendasta/protos/conversation/v1';

export interface AiModeOption {
  mode: AIResponseMode;
  labelKey: string;
}

/**
 * Normalizes an AI mode read off a conversation into the protos string enum. Today the value
 * arrives as the string form — the class SDK's AIConfiguration doesn't declare aiResponseMode, so
 * fromProto's Object.assign passes it through without the enumStringToValue conversion it applies
 * to declared enums. The numeric branch covers the SDK being regenerated with the field declared,
 * which would otherwise silently read as UNSET (pill shows Off).
 */
export function toAiResponseMode(raw: unknown): AIResponseMode {
  if (typeof raw === 'string' && raw in AIResponseModeValue) {
    return raw as AIResponseMode;
  }
  if (typeof raw === 'number') {
    const name = Object.keys(AIResponseModeValue).find((key) => AIResponseModeValue[key as AIResponseMode] === raw) as
      | AIResponseMode
      | undefined;
    if (name) return name;
  }
  return AIResponseMode.AI_RESPONSE_MODE_UNSET;
}

/**
 * The AI mode to show as active. Defensive only: AIConfigurationToProto already derives a concrete
 * mode from the legacy bool for pre-dual-write rows, so UNSET shouldn't reach here. Mirrors that
 * derivation (true → AutoSend, false → Off) rather than showing a blank pill if one slips through.
 */
export function resolveCurrentAiMode(mode: AIResponseMode | undefined, aiResponseEnabled: boolean): AIResponseMode {
  if (mode && mode !== AIResponseMode.AI_RESPONSE_MODE_UNSET) {
    return mode;
  }
  return aiResponseEnabled ? AIResponseMode.AI_RESPONSE_MODE_AUTO_SEND : AIResponseMode.AI_RESPONSE_MODE_OFF;
}

/** Translation key for a single AI mode — used to label the submenu trigger with the current mode. */
export function aiModeLabelKey(mode: AIResponseMode): string {
  switch (mode) {
    case AIResponseMode.AI_RESPONSE_MODE_AUTO_SEND:
      return 'INBOX.SENDER.AI_MODE.AUTO_SEND';
    case AIResponseMode.AI_RESPONSE_MODE_AUTO_DRAFT:
      return 'INBOX.SENDER.AI_MODE.AUTO_DRAFT';
    default:
      return 'INBOX.SENDER.AI_MODE.OFF';
  }
}

/**
 * The AI mode options offered in the conversation top bar. Only reachable behind
 * ai_drafter_enabled, so AutoDraft is always offered. AutoSend is hidden on personal email — the
 * backend rejects that combination outright.
 */
export function getAvailableAiModes(isPersonalEmail: boolean): AiModeOption[] {
  const modes: AIResponseMode[] = [AIResponseMode.AI_RESPONSE_MODE_OFF];
  if (!isPersonalEmail) {
    modes.push(AIResponseMode.AI_RESPONSE_MODE_AUTO_SEND);
  }
  modes.push(AIResponseMode.AI_RESPONSE_MODE_AUTO_DRAFT);
  return modes.map((mode) => ({ mode, labelKey: aiModeLabelKey(mode) }));
}
